<template>
  <section v-loading="loading" class="history-page" aria-label="历史订单">
    <el-scrollbar class="history-scrollbar" always>
      <div v-if="orders.length" class="orders-table" role="table" aria-label="历史订单列表">
        <div class="orders-table__header" role="row">
          <span v-for="column in columns" :key="column.key" role="columnheader">
            {{ column.label }}
          </span>
        </div>

        <div
          v-for="order in orders"
          :key="order.orderNo || `${order.productName}-${order.createdAt}`"
          class="orders-table__row"
          role="row"
        >
          <span class="order-cell order-cell--number" role="cell">
            <span class="order-cell__label" aria-hidden="true">订单号</span>
            <span class="order-cell__value" :title="order.orderNo">
              {{ order.orderNo || '-' }}
            </span>
          </span>
          <span class="order-cell order-cell--product" role="cell">
            <span class="order-cell__label" aria-hidden="true">商品信息</span>
            <span class="order-cell__value" :title="order.productName">
              {{ order.productName || '-' }}
            </span>
          </span>
          <span class="order-cell order-cell--price" role="cell">
            <span class="order-cell__label" aria-hidden="true">价格（元）</span>
            <span class="order-cell__value">{{ order.price || '-' }}</span>
          </span>
          <span class="order-cell order-cell--created" role="cell">
            <span class="order-cell__label" aria-hidden="true">创建时间</span>
            <span class="order-cell__value">{{ order.createdAt || '-' }}</span>
          </span>
          <span class="order-cell order-cell--paid-amount" role="cell">
            <span class="order-cell__label" aria-hidden="true">订单实付（元）</span>
            <span class="order-cell__value">{{ order.paidAmount || '-' }}</span>
          </span>
          <span class="order-cell order-cell--paid-at" role="cell">
            <span class="order-cell__label" aria-hidden="true">付款时间</span>
            <span class="order-cell__value">{{ order.paidAt || '-' }}</span>
          </span>
          <span class="order-cell order-cell--status" role="cell">
            <span class="order-cell__label" aria-hidden="true">交易状态</span>
            <span class="order-cell__value">{{ order.status || '-' }}</span>
          </span>
          <span class="order-cell order-cell--action" role="cell">
            <span class="order-cell__label" aria-hidden="true">交易操作</span>
            <button
              type="button"
              class="invoice-button"
              :class="{ 'invoice-button--view': order.invoiced === 1 }"
              @click="handleInvoiceAction(order)"
            >
              {{ order.invoiced === 1 ? '查看发票' : '申请发票' }}
            </button>
          </span>
        </div>
      </div>

      <div v-else-if="!loading" class="orders-empty" role="status">
        <SvgIcon name="orders-empty" :size="60" color="#6033e1" />
        <span>暂无订单</span>
      </div>
    </el-scrollbar>

    <Teleport to="body">
      <Transition name="invoice-application-dialog">
        <div
          v-if="pendingInvoiceOrder"
          class="invoice-application-overlay"
          role="presentation"
          @click.self="closeInvoiceDialog"
        >
          <section
            v-if="invoiceDialogMode === 'notice'"
            class="invoice-application-dialog invoice-application-dialog--notice"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-application-notice-title"
            aria-describedby="invoice-application-notice-description"
          >
            <header class="invoice-application-dialog__header">
              <h2 id="invoice-application-notice-title">申请发票</h2>
              <button
                type="button"
                class="invoice-application-dialog__close"
                aria-label="关闭申请发票提示"
                @click="closeInvoiceDialog"
              >
                <SvgIcon name="modal-close" :size="11" />
              </button>
            </header>

            <div class="invoice-application-dialog__action">
              <p id="invoice-application-notice-description">
                订单【{{ pendingInvoiceOrder.orderNo }}】未申请发票，可点击按钮进行申请
              </p>
              <button
                type="button"
                class="invoice-application-dialog__apply"
                @click="handleApplyInvoice"
              >
                申请发票
              </button>
            </div>

            <article class="invoice-application-dialog__notice">
              <strong>重要提示：</strong>
              <ol>
                <li>
                  广东省已试点开具全电发票，全电发票没有章以及付款人收款人，但发票都是正规发票，可以在开具24小时后在国税查询发票；
                </li>
                <li>
                  申请增值税普通发票或者专用发票电子版，开好的发票会发送到您填写的邮箱上，一个工作日内开具；
                </li>
                <li>请认真填写名称以及税号，有税号的企业必须填写税号，否则会影响报销；</li>
                <li>
                  因用户发票信息填写错误或要更换发票类型而重新申请的，须额外支付价税和手续费合计的6%。
                </li>
              </ol>
            </article>
          </section>

          <section
            v-else
            class="invoice-application-dialog invoice-application-dialog--form"
            :class="{
              'invoice-application-dialog--personal': isPersonalInvoice,
              'invoice-application-dialog--value-added': isValueAddedInvoice
            }"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-application-form-title"
          >
            <header class="invoice-application-dialog__header">
              <h2 id="invoice-application-form-title">发票申请</h2>
              <button
                type="button"
                class="invoice-application-dialog__close"
                aria-label="关闭发票申请弹窗"
                @click="closeInvoiceDialog"
              >
                <SvgIcon name="modal-close" :size="12" />
              </button>
            </header>

            <form class="invoice-application-form" @submit.prevent="submitInvoiceApplication">
              <div ref="invoiceTypeFieldRef" class="invoice-form-field invoice-type-field">
                <label class="invoice-form-label" for="invoice-type">发票类型</label>
                <div class="invoice-type-row">
                  <div class="invoice-select-wrap">
                    <button
                      id="invoice-type"
                      type="button"
                      class="invoice-select"
                      :class="{ 'invoice-select--open': invoiceTypeDropdownOpen }"
                      aria-haspopup="listbox"
                      :aria-expanded="invoiceTypeDropdownOpen"
                      aria-controls="invoice-type-options"
                      @click="toggleInvoiceTypeDropdown"
                    >
                      <span>{{ selectedInvoiceTypeLabel }}</span>
                      <SvgIcon
                        class="invoice-select__chevron"
                        :class="{ 'invoice-select__chevron--open': invoiceTypeDropdownOpen }"
                        name="create-option-chevron"
                        :size="14"
                        color="#6e6a7c"
                      />
                    </button>

                    <Transition name="invoice-select-dropdown">
                      <ul
                        v-if="invoiceTypeDropdownOpen"
                        id="invoice-type-options"
                        class="invoice-select-dropdown"
                        role="listbox"
                        aria-labelledby="invoice-type"
                      >
                        <li v-for="option in invoiceTypeOptions" :key="option.value" role="none">
                          <button
                            type="button"
                            class="invoice-select-option"
                            :class="{
                              'invoice-select-option--selected':
                                option.value === invoiceForm.invoiceType
                            }"
                            role="option"
                            :aria-selected="option.value === invoiceForm.invoiceType"
                            @click="selectInvoiceType(option.value)"
                          >
                            <span>{{ option.label }}</span>
                            <SvgIcon
                              v-if="option.value === invoiceForm.invoiceType"
                              name="check"
                              :size="14"
                              color="#6033e1"
                            />
                          </button>
                        </li>
                      </ul>
                    </Transition>
                  </div>

                  <button type="button" class="invoice-style-link" @click="handleViewInvoiceStyle">
                    查看样式
                  </button>
                </div>
              </div>

              <div class="invoice-form-field">
                <span class="invoice-form-label">开票内容</span>
                <p class="invoice-content">
                  <span class="invoice-content__category">{{ invoiceContentCategory }}</span>
                  <span>{{ invoiceContentName }}</span>
                </p>
              </div>

              <fieldset
                v-if="showApplicantOptions"
                class="invoice-form-field invoice-applicant-field"
              >
                <legend class="invoice-form-label">申请人</legend>
                <div class="invoice-applicant-options">
                  <label
                    v-for="option in invoiceApplicantOptions"
                    :key="option.value"
                    class="invoice-radio-option"
                  >
                    <input
                      v-model="invoiceForm.applicantType"
                      class="invoice-radio-input"
                      type="radio"
                      name="invoice-applicant-type"
                      :value="option.value"
                    />
                    <span class="invoice-radio-mark" aria-hidden="true"></span>
                    <span>{{ option.label }}</span>
                  </label>
                </div>
              </fieldset>

              <div class="invoice-form-field">
                <label class="invoice-form-label" for="invoice-title">
                  {{ invoiceTitleLabel }}
                  <span class="invoice-required">*</span>
                </label>
                <input
                  id="invoice-title"
                  v-model.trim="invoiceForm.companyName"
                  class="invoice-input"
                  type="text"
                  :placeholder="invoiceTitlePlaceholder"
                />
              </div>

              <div v-if="invoiceForm.applicantType === 'company'" class="invoice-form-field">
                <label class="invoice-form-label" for="invoice-tax-no">
                  税号<span class="invoice-required">*</span>
                </label>
                <input
                  id="invoice-tax-no"
                  v-model.trim="invoiceForm.taxNo"
                  class="invoice-input"
                  type="text"
                  placeholder="请输入税号或社会信用代码"
                />
              </div>

              <div class="invoice-form-field">
                <label class="invoice-form-label" for="invoice-phone">
                  联系手机号<span class="invoice-required">*</span>
                </label>
                <input
                  id="invoice-phone"
                  v-model.trim="invoiceForm.phone"
                  class="invoice-input"
                  type="tel"
                  placeholder="请输入手机号"
                />
              </div>

              <div class="invoice-form-field">
                <label class="invoice-form-label" for="invoice-email">
                  接收邮箱<span class="invoice-required">*</span>
                </label>
                <input
                  id="invoice-email"
                  v-model.trim="invoiceForm.email"
                  class="invoice-input"
                  type="email"
                  placeholder="请输入邮箱"
                />
                <p class="invoice-email-tip">
                  此邮箱主要用于接收电子发票，请务必确认后再点击申请按钮确认
                </p>
              </div>

              <div class="invoice-form-field">
                <label class="invoice-form-label" for="invoice-remark">
                  发票备注（如需备注文字或地址电话开户行账号请在下方备注栏填写）
                </label>
                <textarea
                  id="invoice-remark"
                  v-model.trim="invoiceForm.remark"
                  class="invoice-textarea"
                  placeholder="填写备注"
                ></textarea>
              </div>

              <footer class="invoice-actions">
                <button type="button" class="invoice-actions__cancel" @click="closeInvoiceDialog">
                  取消
                </button>
                <button type="submit" class="invoice-actions__submit" :disabled="applyingInvoice">
                  {{ applyingInvoice ? '提交中...' : '申请发票' }}
                </button>
              </footer>
            </form>
          </section>
        </div>
      </Transition>

      <Transition name="invoice-submit-confirm-dialog">
        <div
          v-if="invoiceSubmitConfirmVisible && pendingInvoiceOrder"
          class="invoice-submit-confirm-overlay"
          role="presentation"
          @click.self="closeInvoiceSubmitConfirm"
        >
          <section
            class="invoice-submit-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-submit-confirm-title"
            aria-describedby="invoice-submit-confirm-description"
          >
            <header class="invoice-submit-confirm-dialog__header">
              <h2 id="invoice-submit-confirm-title">确认申请发票</h2>
              <button
                type="button"
                class="invoice-submit-confirm-dialog__close"
                aria-label="关闭确认弹窗"
                @click="closeInvoiceSubmitConfirm"
              >
                <SvgIcon name="modal-close" :size="12" />
              </button>
            </header>

            <div class="invoice-submit-confirm-dialog__body">
              <p
                id="invoice-submit-confirm-description"
                class="invoice-submit-confirm-dialog__text"
              >
                订单【{{
                  pendingInvoiceOrder.orderNo
                }}】将按当前填写信息申请开票，确认后将立即提交。
              </p>

              <dl class="invoice-submit-confirm-dialog__summary">
                <div>
                  <dt>发票类型</dt>
                  <dd>{{ selectedInvoiceTypeLabel }}</dd>
                </div>
                <div>
                  <dt>名称</dt>
                  <dd>{{ invoiceForm.companyName || '未填写' }}</dd>
                </div>
                <div>
                  <dt>邮箱</dt>
                  <dd>{{ invoiceForm.email || '未填写' }}</dd>
                </div>
              </dl>

              <footer class="invoice-submit-confirm-dialog__actions">
                <button
                  type="button"
                  class="invoice-submit-confirm-dialog__cancel"
                  @click="closeInvoiceSubmitConfirm"
                >
                  取消
                </button>
                <button
                  type="button"
                  class="invoice-submit-confirm-dialog__submit"
                  :disabled="applyingInvoice"
                  @click="confirmInvoiceApplication"
                >
                  {{ applyingInvoice ? '提交中...' : '确认申请' }}
                </button>
              </footer>
            </div>
          </section>
        </div>
      </Transition>

      <Transition name="invoice-style-preview">
        <div
          v-if="invoiceStylePreviewVisible"
          class="invoice-style-preview-overlay"
          role="presentation"
          @click="closeInvoiceStylePreview"
        >
          <section
            class="invoice-style-preview"
            role="dialog"
            aria-modal="true"
            aria-labelledby="invoice-style-preview-title"
          >
            <h2 id="invoice-style-preview-title">{{ invoiceStylePreview.title }}</h2>
            <img
              class="invoice-style-preview__image"
              :src="invoiceStylePreview.image"
              :alt="`${invoiceStylePreview.title}示例`"
              @click.stop
            />
            <p>点击非图片区域关闭预览</p>
          </section>
        </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
  import { ElMessage } from 'element-plus';
  import { useRouter } from 'vue-router';
  import { applyForInvoice } from '@/api/invoice';
  import { fetchPayOrderList } from '@/api/order';
  import invoiceTemplate1 from '@/assets/images/invoice/invoice_template1.jpg';
  import invoiceTemplate2 from '@/assets/images/invoice/invoice_template2.jpg';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import type { PayOrderItem } from '@/api/order';

  interface TableColumn {
    key: string;
    label: string;
  }

  type InvoiceApplicantType = 'company' | 'personal';
  type InvoiceDialogMode = 'notice' | 'form';
  type InvoiceTypeValue = 1 | 4;

  interface InvoiceApplicantOption {
    value: InvoiceApplicantType;
    label: string;
  }

  interface InvoiceTypeOption {
    value: InvoiceTypeValue;
    label: string;
  }

  interface InvoiceApplicationForm {
    invoiceType: InvoiceTypeValue;
    applicantType: InvoiceApplicantType;
    companyName: string;
    taxNo: string;
    phone: string;
    email: string;
    remark: string;
  }

  const router = useRouter();
  const orders = ref<PayOrderItem[]>([]);
  const loading = ref(false);
  const pendingInvoiceOrder = ref<PayOrderItem | null>(null);
  const invoiceDialogMode = ref<InvoiceDialogMode>('notice');
  const applyingInvoice = ref(false);
  const invoiceTypeDropdownOpen = ref(false);
  const invoiceSubmitConfirmVisible = ref(false);
  const invoiceStylePreviewVisible = ref(false);
  const invoiceTypeFieldRef = ref<HTMLElement | null>(null);
  const invoiceContentCategory = '*其他软件服务*';
  const invoiceContentName = '云展网电子杂志制作平台V3.0';
  const invoiceTypeOptions: InvoiceTypeOption[] = [
    { value: 1, label: '增值税普通发票电子版' },
    { value: 4, label: '增值税专用发票电子版' }
  ];
  const invoiceApplicantOptions: InvoiceApplicantOption[] = [
    { value: 'company', label: '公司 / 事业单位' },
    { value: 'personal', label: '个人 / 非企业单位' }
  ];
  const invoiceForm = reactive<InvoiceApplicationForm>(createDefaultInvoiceForm());
  const isValueAddedInvoice = computed(() => invoiceForm.invoiceType === 4);
  const showApplicantOptions = computed(() => !isValueAddedInvoice.value);
  const isPersonalInvoice = computed(() => invoiceForm.applicantType === 'personal');
  const selectedInvoiceTypeLabel = computed(
    () =>
      invoiceTypeOptions.find((option) => option.value === invoiceForm.invoiceType)?.label ??
      invoiceTypeOptions[0].label
  );
  const invoiceStylePreview = computed(() =>
    invoiceForm.invoiceType === 4
      ? { title: '增值税专用发票', image: invoiceTemplate2 }
      : { title: '增值税普通发票', image: invoiceTemplate1 }
  );
  const invoiceTitleLabel = computed(() => (isPersonalInvoice.value ? '名称' : '公司名称'));
  const invoiceTitlePlaceholder = computed(() =>
    isPersonalInvoice.value ? '请输入“个人”或单位名称:' : '请输入公司名称'
  );
  let latestRequestId = 0;

  const columns: TableColumn[] = [
    { key: 'orderNo', label: '订单号' },
    { key: 'productName', label: '商品信息' },
    { key: 'price', label: '价格（元）' },
    { key: 'createdAt', label: '创建时间' },
    { key: 'paidAmount', label: '订单实付（元）' },
    { key: 'paidAt', label: '付款时间' },
    { key: 'status', label: '交易状态' },
    { key: 'action', label: '交易操作' }
  ];

  onMounted(() => {
    void loadOrders();
    document.addEventListener('keydown', handleDialogKeydown);
    document.addEventListener('click', handleDocumentClick);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleDialogKeydown);
    document.removeEventListener('click', handleDocumentClick);
  });

  async function loadOrders() {
    const requestId = latestRequestId + 1;
    latestRequestId = requestId;
    loading.value = true;

    try {
      const result = await fetchPayOrderList({
        type: 1,
        status: 1,
        system_type: '2,9',
        page: 0
      });

      if (requestId !== latestRequestId) return;

      orders.value = result.list;
    } catch (error) {
      if (requestId !== latestRequestId) return;

      console.error('获取历史订单失败', error);
      ElMessage.error(getErrorMessage(error, '获取历史订单失败，请稍后重试'));
    } finally {
      if (requestId === latestRequestId) loading.value = false;
    }
  }

  function handleInvoiceAction(order: PayOrderItem) {
    if (order.invoiced === 0) {
      openInvoiceNotice(order);
      return;
    }

    void navigateToInvoice(order);
  }

  function openInvoiceNotice(order: PayOrderItem) {
    invoiceSubmitConfirmVisible.value = false;
    resetInvoiceForm();
    pendingInvoiceOrder.value = order;
    invoiceDialogMode.value = 'notice';
  }

  function handleApplyInvoice() {
    if (!pendingInvoiceOrder.value) return;

    invoiceDialogMode.value = 'form';
  }

  function closeInvoiceDialog() {
    invoiceTypeDropdownOpen.value = false;
    invoiceSubmitConfirmVisible.value = false;
    invoiceStylePreviewVisible.value = false;
    pendingInvoiceOrder.value = null;
    invoiceDialogMode.value = 'notice';
    resetInvoiceForm();
  }

  function closeInvoiceSubmitConfirm() {
    invoiceSubmitConfirmVisible.value = false;
  }

  function toggleInvoiceTypeDropdown() {
    invoiceTypeDropdownOpen.value = !invoiceTypeDropdownOpen.value;
  }

  function selectInvoiceType(value: InvoiceTypeValue) {
    invoiceForm.invoiceType = value;
    if (value === 4) invoiceForm.applicantType = 'company';
    invoiceTypeDropdownOpen.value = false;
  }

  function submitInvoiceApplication() {
    if (applyingInvoice.value) return;

    if (invoiceDialogMode.value !== 'form') {
      invoiceDialogMode.value = 'form';
      return;
    }

    const errorMessage = validateInvoiceForm();

    if (errorMessage) {
      ElMessage.warning(errorMessage);
      return;
    }

    const order = pendingInvoiceOrder.value;

    if (!order) {
      ElMessage.warning('未找到可申请发票的订单');
      return;
    }

    invoiceSubmitConfirmVisible.value = true;
  }

  async function confirmInvoiceApplication() {
    if (applyingInvoice.value) return;

    const order = pendingInvoiceOrder.value;

    if (!order) {
      ElMessage.warning('未找到可申请发票的订单');
      return;
    }

    applyingInvoice.value = true;

    try {
      await applyForInvoice({
        iType: invoiceForm.invoiceType,
        number: invoiceForm.applicantType === 'company' ? invoiceForm.taxNo : '',
        title: invoiceForm.companyName,
        reEmail: invoiceForm.email,
        rePhone: invoiceForm.phone,
        remarks: invoiceForm.remark,
        regieterAddressTel: '',
        regieterTel: '',
        bankAccount: '',
        bankOpen: '',
        addressee: '',
        address: '',
        phone: '',
        postcode: '',
        pays: order.orderNo
      });

      ElMessage.success('发票申请已提交');
      closeInvoiceDialog();
      void loadOrders();
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '发票申请提交失败，请稍后重试'));
    } finally {
      applyingInvoice.value = false;
    }
  }

  function handleViewInvoiceStyle() {
    invoiceTypeDropdownOpen.value = false;
    invoiceStylePreviewVisible.value = true;
  }

  function closeInvoiceStylePreview() {
    invoiceStylePreviewVisible.value = false;
  }

  function handleDialogKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && invoiceStylePreviewVisible.value) {
      closeInvoiceStylePreview();
      return;
    }

    if (event.key === 'Escape' && invoiceSubmitConfirmVisible.value) {
      closeInvoiceSubmitConfirm();
      return;
    }

    if (event.key === 'Escape' && invoiceTypeDropdownOpen.value) {
      invoiceTypeDropdownOpen.value = false;
      return;
    }

    if (event.key === 'Escape' && pendingInvoiceOrder.value) closeInvoiceDialog();
  }

  function handleDocumentClick(event: MouseEvent) {
    if (!invoiceTypeDropdownOpen.value) return;

    const target = event.target;
    if (target instanceof Node && invoiceTypeFieldRef.value?.contains(target)) return;

    invoiceTypeDropdownOpen.value = false;
  }

  async function navigateToInvoice(order: PayOrderItem) {
    try {
      await router.push({ name: 'orders-invoice' });
    } catch {
      ElMessage.error(
        order.invoiced === 1 ? '跳转发票历史失败，请稍后重试' : '跳转申请发票失败，请稍后重试'
      );
    }
  }

  function createDefaultInvoiceForm(): InvoiceApplicationForm {
    return {
      invoiceType: 1,
      applicantType: 'company',
      companyName: '',
      taxNo: '',
      phone: '',
      email: '',
      remark: ''
    };
  }

  function resetInvoiceForm() {
    Object.assign(invoiceForm, createDefaultInvoiceForm());
  }

  function validateInvoiceForm() {
    if (!invoiceForm.companyName) {
      return invoiceForm.applicantType === 'company' ? '请输入公司名称' : '请输入“个人”或单位名称:';
    }

    if (invoiceForm.applicantType === 'company' && !invoiceForm.taxNo)
      return '请输入税号或社会信用代码';

    if (!/^1[3-9]\d{9}$/.test(invoiceForm.phone)) return '请输入正确的联系手机号';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceForm.email)) return '请输入正确的接收邮箱';

    return '';
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (typeof error === 'object' && error !== null) {
      const errorRecord = error as Record<string, unknown>;
      if (typeof errorRecord.errorMsg === 'string' && errorRecord.errorMsg) {
        return errorRecord.errorMsg;
      }
      if (typeof errorRecord.msg === 'string' && errorRecord.msg) return errorRecord.msg;
      if (typeof errorRecord.message === 'string' && errorRecord.message) {
        return errorRecord.message;
      }
    }

    if (error instanceof Error && error.message) return error.message;

    return fallback;
  }
</script>

<style scoped>
  .history-page {
    flex: 1;
    width: 100%;
    min-height: 0;
    display: flex;
    overflow: hidden;
  }

  .history-scrollbar {
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    --el-scrollbar-bg-color: rgba(96, 51, 225, 0.34);
    --el-scrollbar-hover-bg-color: rgba(96, 51, 225, 0.52);
  }

  .history-scrollbar :deep(.el-scrollbar__wrap) {
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  .history-scrollbar :deep(.el-scrollbar__view) {
    min-height: 100%;
    display: flex;
  }

  .history-scrollbar :deep(.el-scrollbar__bar.is-vertical) {
    right: 2px;
  }

  .orders-table {
    flex: 1 0 auto;
    width: 100%;
    min-width: 1080px;
    color: #160211;
    font-size: 14px;
  }

  .orders-table__header,
  .orders-table__row {
    display: grid;
    grid-template-columns:
      minmax(170px, 1.45fr) minmax(160px, 1.35fr) minmax(72px, 0.65fr)
      minmax(120px, 0.9fr) minmax(96px, 0.8fr) minmax(120px, 0.9fr)
      minmax(64px, 0.55fr) 80px;
    align-items: center;
    column-gap: clamp(16px, 1.7vw, 24px);
    padding: 0 clamp(24px, 2.3vw, 32px);
    box-sizing: border-box;
  }

  .orders-table__header {
    height: 44px;
    border-radius: 12px;
    background: #f2eff9;
    color: #6e6a7c;
    line-height: 20px;
  }

  .orders-table__row {
    min-height: 84px;
    border-bottom: 1px solid #eff0f7;
    line-height: 22px;
  }

  .orders-empty {
    flex: 1;
    width: 100%;
    min-height: 160px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    color: #6e6a7c;
    font-size: 18px;
    line-height: 14px;
  }

  .order-cell {
    min-width: 0;
  }

  .order-cell__label {
    display: none;
  }

  .order-cell__value {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .order-cell--created .order-cell__value,
  .order-cell--paid-at .order-cell__value {
    white-space: normal;
  }

  .order-cell--action {
    position: sticky;
    right: 0;
    z-index: 1;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: #fbf9ff;
  }

  .orders-table__header span:last-child {
    position: sticky;
    right: 0;
    z-index: 1;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-radius: 0 12px 12px 0;
    background: #f2eff9;
  }

  .invoice-button {
    width: 80px;
    height: 36px;
    padding: 0 12px;
    border: 0;
    border-radius: 8px;
    background: rgba(96, 51, 225, 0.08);
    color: #6033e1;
    font: inherit;
    line-height: 14px;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .invoice-button:hover {
    background: rgba(96, 51, 225, 0.14);
  }

  .invoice-button--view {
    background: rgba(48, 216, 136, 0.1);
    color: #30d888;
    line-height: 12px;
  }

  .invoice-button--view:hover {
    background: rgba(48, 216, 136, 0.16);
  }

  .invoice-application-overlay {
    position: fixed;
    inset: 0;
    z-index: 4000;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.5);
  }

  .invoice-application-dialog {
    max-width: 100%;
    max-height: calc(100vh - 48px);
    box-sizing: border-box;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .invoice-application-dialog--notice {
    width: 623px;
    height: 500px;
    overflow-y: auto;
  }

  .invoice-application-dialog--form {
    width: 508px;
    height: 944px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .invoice-application-dialog--personal {
    height: 855px;
  }

  .invoice-application-dialog--value-added {
    height: 866px;
  }

  .invoice-application-dialog__header {
    height: 73px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 24px 16px;
    box-sizing: border-box;
    border-bottom: 1px solid #eff0f7;
  }

  .invoice-application-dialog__header h2 {
    margin: 0;
    color: #160211;
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
  }

  .invoice-application-dialog__close {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #6e6a7c;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .invoice-application-dialog__close:hover {
    background: #f5f3fd;
    color: #6033e1;
  }

  .invoice-application-dialog--notice .invoice-application-dialog__header {
    height: 74px;
    border-bottom: 0;
  }

  .invoice-application-dialog--notice .invoice-application-dialog__header h2 {
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
  }

  .invoice-application-dialog--notice .invoice-application-dialog__close {
    width: 33px;
    border-radius: 50%;
    color: #999;
  }

  .invoice-application-dialog__action {
    height: 157px;
    margin: 7px 26px 0;
    padding-top: 21px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .invoice-application-dialog__action p {
    max-width: 100%;
    margin: 0;
    color: #000;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    text-align: center;
  }

  .invoice-application-dialog__apply {
    width: 120px;
    height: 40px;
    margin-top: 34px;
    padding: 0 24px;
    border: 0;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    font-family: 'Microsoft YaHei', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
  }

  .invoice-application-dialog__apply:hover {
    background: #5128c7;
  }

  .invoice-application-dialog__notice {
    height: 230px;
    margin: 12px 21px 20px 15px;
    padding: 18px 7px 6px 10px;
    box-sizing: border-box;
    border-radius: 16px;
    background: #f2eff9;
  }

  .invoice-application-dialog__notice strong {
    display: block;
    color: #333940;
    font-size: 14px;
    font-weight: 500;
    line-height: 14px;
  }

  .invoice-application-dialog__notice ol {
    margin: 12px 0 0;
    padding-left: 22px;
    color: #6e6a7c;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }

  .invoice-submit-confirm-overlay {
    position: fixed;
    inset: 0;
    z-index: 4100;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 24px;
    background: rgba(0, 0, 0, 0.35);
  }

  .invoice-submit-confirm-dialog {
    width: 420px;
    max-width: 100%;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    color: #160211;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .invoice-submit-confirm-dialog__header {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 12px 24px;
    box-sizing: border-box;
    border-bottom: 1px solid #eff0f7;
  }

  .invoice-submit-confirm-dialog__header h2 {
    margin: 0;
    color: #160211;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }

  .invoice-submit-confirm-dialog__close {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #6e6a7c;
    cursor: pointer;
    transition:
      background 0.15s,
      color 0.15s;
  }

  .invoice-submit-confirm-dialog__close:hover {
    background: #f5f3fd;
    color: #6033e1;
  }

  .invoice-submit-confirm-dialog__body {
    padding: 20px 24px 24px;
  }

  .invoice-submit-confirm-dialog__text {
    margin: 0;
    color: #160211;
    font-size: 14px;
    line-height: 22px;
  }

  .invoice-submit-confirm-dialog__summary {
    margin: 16px 0 0;
    padding: 12px 14px;
    border-radius: 12px;
    background: #f8f7fc;
  }

  .invoice-submit-confirm-dialog__summary div + div {
    margin-top: 10px;
  }

  .invoice-submit-confirm-dialog__summary dt {
    margin: 0 0 4px;
    color: #6e6a7c;
    font-size: 12px;
    line-height: 18px;
  }

  .invoice-submit-confirm-dialog__summary dd {
    margin: 0;
    color: #160211;
    font-size: 14px;
    line-height: 21px;
    word-break: break-all;
  }

  .invoice-submit-confirm-dialog__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 20px;
  }

  .invoice-submit-confirm-dialog__cancel,
  .invoice-submit-confirm-dialog__submit {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 16px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  .invoice-submit-confirm-dialog__cancel {
    border: 1px solid #eff0f7;
    background: #fff;
    color: #6e6a7c;
  }

  .invoice-submit-confirm-dialog__cancel:hover {
    border-color: #d9d4e8;
    background: #f8f7fc;
  }

  .invoice-submit-confirm-dialog__submit {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .invoice-submit-confirm-dialog__submit:hover {
    background: #5128c7;
  }

  .invoice-submit-confirm-dialog__submit:disabled {
    background: #c4c0d0;
    cursor: not-allowed;
  }

  .invoice-style-preview-overlay {
    position: fixed;
    inset: 0;
    z-index: 4200;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    padding: 32px;
    overflow-y: auto;
    background: rgba(0, 0, 0, 0.72);
    cursor: zoom-out;
  }

  .invoice-style-preview {
    width: min(920px, 100%);
    max-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #fff;
    font-family: 'Source Han Sans CN VF', 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  }

  .invoice-style-preview h2 {
    margin: 0 0 20px;
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
  }

  .invoice-style-preview__image {
    display: block;
    width: auto;
    max-width: 100%;
    max-height: calc(100vh - 172px);
    max-height: calc(100dvh - 172px);
    object-fit: contain;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 20px 48px rgba(0, 0, 0, 0.32);
    cursor: default;
  }

  .invoice-style-preview p {
    margin: 20px 0 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 13px;
    line-height: 20px;
  }

  .invoice-style-preview-enter-active,
  .invoice-style-preview-leave-active {
    transition: opacity 0.15s ease;
  }

  .invoice-style-preview-enter-from,
  .invoice-style-preview-leave-to {
    opacity: 0;
  }

  .invoice-application-form {
    flex: 1;
    min-height: 0;
    padding: 20px 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
    overflow-y: auto;
  }

  .invoice-application-form::-webkit-scrollbar {
    width: 6px;
  }

  .invoice-application-form::-webkit-scrollbar-track {
    background: transparent;
  }

  .invoice-application-form::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: rgba(96, 51, 225, 0.22);
  }

  .invoice-form-field {
    margin: 0;
    padding: 0;
    border: 0;
    min-width: 0;
  }

  .invoice-form-label {
    display: block;
    margin: 0 0 8px;
    color: #160211;
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }

  .invoice-required {
    color: #ff4d4f;
  }

  .invoice-type-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .invoice-select-wrap {
    position: relative;
    width: 354px;
    max-width: 100%;
  }

  .invoice-select {
    width: 100%;
    height: 39px;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 12px 0 14px;
    border: 1px solid #eff0f7;
    border-radius: 10px;
    background: #fff;
    color: #000;
    font-family: inherit;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      box-shadow 0.15s;
  }

  .invoice-select:hover,
  .invoice-select--open {
    border-color: rgba(96, 51, 225, 0.45);
  }

  .invoice-select--open {
    box-shadow: 0 0 0 3px rgba(96, 51, 225, 0.08);
  }

  .invoice-select__chevron {
    transition: transform 0.15s;
  }

  .invoice-select__chevron--open {
    transform: rotate(180deg);
  }

  .invoice-select-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 2;
    width: 100%;
    margin: 0;
    padding: 6px;
    box-sizing: border-box;
    border: 1px solid #eff0f7;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 12px 28px rgba(22, 2, 17, 0.12);
    list-style: none;
  }

  .invoice-select-option {
    width: 100%;
    min-height: 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 7px 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: #160211;
    font-family: inherit;
    font-size: 14px;
    line-height: 22px;
    text-align: left;
    cursor: pointer;
  }

  .invoice-select-option:hover,
  .invoice-select-option--selected {
    background: #f5f3fd;
    color: #6033e1;
  }

  .invoice-select-dropdown-enter-active,
  .invoice-select-dropdown-leave-active {
    transition:
      opacity 0.12s ease,
      transform 0.12s ease;
  }

  .invoice-select-dropdown-enter-from,
  .invoice-select-dropdown-leave-to {
    opacity: 0;
    transform: translateY(-4px);
  }

  .invoice-style-link {
    flex: 0 0 auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: #6033e1;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
    cursor: pointer;
  }

  .invoice-content {
    min-height: 38px;
    display: flex;
    align-items: center;
    margin: 0;
    padding: 8px 2px;
    box-sizing: border-box;
    border-radius: 10px;
    color: #160211;
    font-size: 14px;
    line-height: 22px;
  }

  .invoice-content__category {
    color: #6033e1;
  }

  .invoice-applicant-field .invoice-form-label {
    float: none;
  }

  .invoice-applicant-options {
    display: flex;
    align-items: center;
    gap: 24px;
    min-height: 21px;
    flex-wrap: wrap;
  }

  .invoice-radio-option {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #160211;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    cursor: pointer;
  }

  .invoice-radio-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .invoice-radio-mark {
    position: relative;
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border: 2px solid #c4c0d0;
    border-radius: 50%;
    background: #fff;
  }

  .invoice-radio-mark::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: #fff;
    opacity: 0;
  }

  .invoice-radio-input:checked + .invoice-radio-mark {
    border-color: #6033e1;
    background: #6033e1;
  }

  .invoice-radio-input:checked + .invoice-radio-mark::after {
    opacity: 1;
  }

  .invoice-input,
  .invoice-textarea {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #eff0f7;
    border-radius: 10px;
    color: #160211;
    font-family: inherit;
    font-size: 14px;
    line-height: 21px;
    transition: border-color 0.15s;
  }

  .invoice-input {
    height: 39px;
    padding: 0 12px;
    background: #fff;
  }

  .invoice-textarea {
    height: 81px;
    padding: 8px 12px;
    resize: none;
    background: #f8f7fc;
  }

  .invoice-input::placeholder,
  .invoice-textarea::placeholder {
    color: #c4c0d0;
  }

  .invoice-input:focus,
  .invoice-textarea:focus {
    border-color: #6033e1;
    outline: 0;
  }

  .invoice-email-tip {
    margin: 8px 0 0;
    padding: 8px 12px;
    box-sizing: border-box;
    border: 1px solid #ffe4b5;
    border-radius: 10px;
    background: #fff8ee;
    color: #d38743;
    font-size: 12px;
    line-height: 20px;
  }

  .invoice-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 4px;
  }

  .invoice-actions__cancel,
  .invoice-actions__submit {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 16px;
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    cursor: pointer;
    transition:
      background 0.15s,
      border-color 0.15s;
  }

  .invoice-actions__cancel {
    border: 1px solid #eff0f7;
    background: #fff;
    color: #6e6a7c;
  }

  .invoice-actions__cancel:hover {
    border-color: #d9d4e8;
    background: #f8f7fc;
  }

  .invoice-actions__submit {
    border: 0;
    background: #6033e1;
    color: #fff;
  }

  .invoice-actions__submit:hover {
    background: #5128c7;
  }

  .invoice-actions__submit:disabled {
    background: #c4c0d0;
    cursor: not-allowed;
  }

  .invoice-application-dialog__close:focus-visible,
  .invoice-application-dialog__apply:focus-visible,
  .invoice-select:focus-visible,
  .invoice-select-option:focus-visible,
  .invoice-style-link:focus-visible,
  .invoice-radio-input:focus-visible + .invoice-radio-mark,
  .invoice-actions__cancel:focus-visible,
  .invoice-actions__submit:focus-visible,
  .invoice-submit-confirm-dialog__close:focus-visible,
  .invoice-submit-confirm-dialog__cancel:focus-visible,
  .invoice-submit-confirm-dialog__submit:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .invoice-submit-confirm-dialog-enter-active,
  .invoice-submit-confirm-dialog-leave-active {
    transition: opacity 0.15s ease;
  }

  .invoice-submit-confirm-dialog-enter-from,
  .invoice-submit-confirm-dialog-leave-to {
    opacity: 0;
  }

  .invoice-application-dialog-enter-active,
  .invoice-application-dialog-leave-active {
    transition: opacity 0.15s ease;
  }

  .invoice-application-dialog-enter-from,
  .invoice-application-dialog-leave-to {
    opacity: 0;
  }

  @media (max-width: 1439px) and (min-width: 768px) {
    .orders-table {
      min-width: 960px;
    }

    .orders-table__header,
    .orders-table__row {
      grid-template-columns:
        minmax(150px, 1.35fr) minmax(140px, 1.25fr) minmax(64px, 0.6fr)
        minmax(105px, 0.85fr) minmax(84px, 0.72fr) minmax(105px, 0.85fr)
        minmax(60px, 0.52fr) 80px;
      column-gap: 12px;
      padding: 0 16px;
    }
  }

  @media (max-width: 767px) {
    .history-page {
      display: flex;
    }

    .history-scrollbar :deep(.el-scrollbar__wrap) {
      overflow-x: hidden;
    }

    .orders-table {
      flex: 1 0 auto;
      position: relative;
      min-width: 0;
      display: grid;
      gap: 12px;
      font-size: 14px;
    }

    .orders-table__header {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .orders-table__row {
      min-height: 0;
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      grid-template-areas:
        'number status'
        'product price'
        'created paid-amount'
        'paid-at action';
      align-items: start;
      gap: 14px 20px;
      width: 100%;
      padding: 16px;
      box-sizing: border-box;
      border: 1px solid #eff0f7;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 2px 10px rgba(22, 2, 17, 0.04);
      line-height: 21px;
    }

    .order-cell {
      display: block;
      min-width: 0;
    }

    .order-cell--number {
      grid-area: number;
    }

    .order-cell--product {
      grid-area: product;
    }

    .order-cell--price {
      grid-area: price;
    }

    .order-cell--created {
      grid-area: created;
    }

    .order-cell--paid-amount {
      grid-area: paid-amount;
    }

    .order-cell--paid-at {
      grid-area: paid-at;
    }

    .order-cell--status {
      grid-area: status;
    }

    .order-cell--action {
      position: static;
      grid-area: action;
      justify-self: end;
      background: transparent;
      text-align: right;
    }

    .order-cell__label {
      display: block;
      margin-bottom: 3px;
      color: #8b8798;
      font-size: 12px;
      line-height: 18px;
    }

    .order-cell__value,
    .order-cell--created .order-cell__value,
    .order-cell--paid-at .order-cell__value {
      overflow: visible;
      text-overflow: clip;
      white-space: normal;
      overflow-wrap: anywhere;
    }

    .order-cell--number .order-cell__value,
    .order-cell--product .order-cell__value {
      color: #160211;
      font-weight: 500;
    }

    .order-cell--status {
      text-align: right;
    }

    .order-cell--status .order-cell__value {
      color: #6033e1;
      font-weight: 500;
    }

    .invoice-button {
      height: 36px;
    }

    .invoice-application-overlay {
      padding-top: max(16px, env(safe-area-inset-top));
      padding-right: max(16px, env(safe-area-inset-right));
      padding-bottom: max(16px, env(safe-area-inset-bottom));
      padding-left: max(16px, env(safe-area-inset-left));
    }

    .invoice-application-dialog {
      width: 100%;
      max-height: calc(100vh - 32px);
      max-height: calc(100dvh - 32px);
    }

    .invoice-application-dialog--notice {
      height: auto;
    }

    .invoice-application-dialog--form {
      height: calc(100vh - 32px);
      height: calc(100dvh - 32px);
    }

    .invoice-application-dialog__header {
      height: 64px;
      padding: 12px 10px 8px 16px;
    }

    .invoice-application-dialog__close {
      width: 44px;
      height: 44px;
    }

    .invoice-application-dialog__action {
      height: auto;
      min-height: 145px;
      margin: 0 16px;
      padding: 18px 0 24px;
    }

    .invoice-application-dialog__apply {
      margin-top: 24px;
    }

    .invoice-application-dialog__notice {
      height: auto;
      margin: 0 12px 16px;
      padding: 16px 12px;
    }

    .invoice-application-form {
      padding: 16px;
      gap: 16px;
    }

    .invoice-input,
    .invoice-textarea {
      font-size: 16px;
    }

    .invoice-type-row {
      align-items: flex-start;
      flex-direction: column;
    }

    .invoice-select-wrap,
    .invoice-select {
      width: 100%;
    }

    .invoice-applicant-options {
      gap: 12px;
    }

    .invoice-actions {
      position: sticky;
      bottom: -16px;
      z-index: 2;
      gap: 12px;
      margin: 0 -16px -16px;
      padding: 12px 16px max(16px, env(safe-area-inset-bottom));
      border-top: 1px solid #eff0f7;
      background: #fff;
    }

    .invoice-actions__cancel,
    .invoice-actions__submit {
      min-width: 0;
    }

    .invoice-submit-confirm-overlay {
      padding-top: max(16px, env(safe-area-inset-top));
      padding-right: max(16px, env(safe-area-inset-right));
      padding-bottom: max(16px, env(safe-area-inset-bottom));
      padding-left: max(16px, env(safe-area-inset-left));
    }

    .invoice-submit-confirm-dialog {
      width: 100%;
      max-height: calc(100vh - 32px);
      max-height: calc(100dvh - 32px);
      overflow-y: auto;
    }

    .invoice-submit-confirm-dialog__header {
      height: 60px;
      padding: 8px 10px 4px 20px;
    }

    .invoice-submit-confirm-dialog__close {
      width: 44px;
      height: 44px;
    }

    .invoice-submit-confirm-dialog__body {
      padding: 16px 20px 20px;
    }

    .invoice-style-preview-overlay {
      padding-top: max(20px, env(safe-area-inset-top));
      padding-right: max(16px, env(safe-area-inset-right));
      padding-bottom: max(20px, env(safe-area-inset-bottom));
      padding-left: max(16px, env(safe-area-inset-left));
    }

    .invoice-style-preview h2 {
      margin-bottom: 16px;
      font-size: 16px;
      line-height: 24px;
    }

    .invoice-style-preview__image {
      max-height: calc(100vh - 144px);
      max-height: calc(100dvh - 144px);
    }

    .invoice-style-preview p {
      margin-top: 16px;
    }
  }
</style>
